import { IonItemGroup } from '@ionic/react';
import { useFormikContext } from 'formik';
import { capitalize } from 'lodash';
import { ReactElement } from 'react';
import { RolePermissionFormField } from '../../api/graphql/generated';
import { i18nKeyType } from '../../i18n';
import { getLocalizedText } from '../../i18n/i18n.utils';
import { PickerInput } from '../../scratchpad/application/shared/component/widget/picker-input';
import { SwitchInput } from '../../scratchpad/application/shared/component/widget/switch-input';
import { PickerOption } from '../../scratchpad/application/shared/component/widget/widget.types';
import { RolePermissionFieldValueInput } from './role-permission-field-value-input';
import {
  RolePermissionInputToLocalizedKey,
  RolePermissionInputType,
  RolePermissionTemplateMetadata,
} from './role.types';

interface RolePermissionFormFieldInputProps {
  field: RolePermissionFormField;
  showLine?: boolean;
}

const RolePermissionFormFieldInput = (
  props: RolePermissionFormFieldInputProps
): ReactElement => {
  const { field } = props;

  const metadata: RolePermissionTemplateMetadata = field.template.metadata;

  if (metadata.rolePermissionInputType.length > 1) {
    return <RolePermissionMultiTypeInput field={field} />;
  } else {
    return (
      <RolePermissionSingleTypeInput
        field={field}
        type={metadata.rolePermissionInputType[0]}
      />
    );
  }
};

interface RolePermissionMultiTypeInputProps {
  field: RolePermissionFormField;
}

/**
 * This component is used to render a field that has multiple input types
 * to select from.
 */
const RolePermissionMultiTypeInput = (
  props: RolePermissionMultiTypeInputProps
): ReactElement => {
  const { field } = props;
  const metadata = field.template.metadata as RolePermissionTemplateMetadata;

  const { getFieldMeta } = useFormikContext();
  const { value: enabled = false } = getFieldMeta<boolean>(
    `${field.template.policy}.can`
  );
  const { value: inputType } = getFieldMeta<
    RolePermissionInputType | undefined
  >(`${field.template.policy}.rolePermissionInputType`);

  // we don't want to show the select type as an option
  const validInputTypes = metadata.rolePermissionInputType.filter(
    (inputType) => inputType !== RolePermissionInputType.SELECT
  );

  const inputTypeOptions: PickerOption[] = validInputTypes.map((inputType) => {
    return {
      label:
        RolePermissionInputToLocalizedKey[inputType] ?? capitalize(inputType),
      value: inputType,
    };
  });

  return (
    <>
      <SwitchInput
        name={`${field.template.policy}.can`}
        label={getLocalizedText(metadata.fieldName as i18nKeyType)}
        description={getLocalizedText('common:numeric_permission_description')}
        showDescription={
          getFieldMeta<boolean>(`${field.template.policy}.can`).value ?? false
        }
      />
      {enabled && (
        <>
          <IonItemGroup>
            <PickerInput
              name={`${field.template.policy}.rolePermissionInputType`}
              options={inputTypeOptions}
              label={getLocalizedText('common:modification_type')}
              ionItemProps={{ color: 'light', lines: 'none' }}
            />
            <RolePermissionFieldValueInput
              field={field}
              type={inputType ?? validInputTypes[0]}
            />
          </IonItemGroup>
        </>
      )}
    </>
  );
};

interface RolePermissionSingleTypeInputProps {
  field: RolePermissionFormField;
  type: RolePermissionInputType;
}

/**
 * This component is used to render a field that has a single input type
 */
const RolePermissionSingleTypeInput = (
  props: RolePermissionSingleTypeInputProps
): ReactElement => {
  const { field, type } = props;
  const metadata = field.template.metadata as RolePermissionTemplateMetadata;

  const { getFieldMeta } = useFormikContext();
  const { value: enabled = false } = getFieldMeta<boolean>(
    `${field.template.policy}.can`
  );

  return (
    <>
      <SwitchInput
        name={`${field.template.policy}.can`}
        label={getLocalizedText(metadata.fieldName as i18nKeyType)}
      />
      {enabled && <RolePermissionFieldValueInput field={field} type={type} />}
    </>
  );
};

export { RolePermissionFormFieldInput };
